import React, { useState, useMemo } from 'react';
import { Search, QrCode, User, MapPin, AlertCircle, Phone, ArrowLeft, Filter, CheckCircle2 } from 'lucide-react';

// ✅ CONEXIÓN AL SISTEMA CENTRAL (Ruta Absoluta)
import { useGlobal } from 'context/GlobalContext';

const BuscadorGlobal = () => {
  // 1. Consumimos los datos reales de todos los niños para permitir búsquedas cruzadas entre salones
  const { ninos } = useGlobal();
  
  const [modo, setModo] = useState('manual'); 
  const [busqueda, setBusqueda] = useState('');
  const [filtroActivo, setFiltroActivo] = useState('Todos');
  const [ninoSeleccionadoId, setNinoSeleccionadoId] = useState(null);

  // 2. LÓGICA DE FILTRADO (Busca en toda la base de datos de IgleKids)
  const ninosFiltrados = useMemo(() => {
    return ninos.filter(nino => {
      const termino = busqueda.toLowerCase();
      const coincideTexto = 
        nino.nombre?.toLowerCase().includes(termino) ||
        (nino.alerta && nino.alerta.toLowerCase().includes(termino)) ||
        nino.salon?.toLowerCase().includes(termino);

      let coincideFiltro = true;
      if (filtroActivo === 'En Salón') {
        coincideFiltro = nino.estatus === 'en_salon';
      } else if (filtroActivo === 'Salud') {
        coincideFiltro = nino.especial === true || (nino.alerta && nino.alerta !== '');
      }

      return coincideTexto && coincideFiltro;
    });
  }, [ninos, busqueda, filtroActivo]);

  const ninoInfo = ninos.find(n => n.id === ninoSeleccionadoId);

  const verFicha = (id) => {
    setNinoSeleccionadoId(id);
    setModo('resultado');
  };

  // VISTA 1: DIRECTORIO DE EXPLORADORES
  const renderManual = () => (
    <div className="space-y-6 animate-in fade-in duration-500 text-left">
      <div className="relative group">
        <div className="absolute -inset-1 bg-indigo-500 rounded-2xl blur opacity-10 group-hover:opacity-20 transition duration-1000"></div>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <input 
            type="text"
            placeholder="Buscar por nombre o salón..."
            className="w-full pl-12 pr-4 py-4 bg-white rounded-2xl border-2 border-transparent shadow-sm focus:border-indigo-500 transition-all outline-none font-bold text-slate-800"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
        {['Todos', 'En Salón', 'Salud'].map(filtro => (
          <button 
            key={filtro} 
            onClick={() => setFiltroActivo(filtro)}
            className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all border-2 ${
              filtroActivo === filtro 
                ? 'bg-slate-900 text-white border-slate-900 shadow-lg' 
                : 'bg-white text-slate-400 border-slate-100'
            }`}
          >
            {filtro}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {ninosFiltrados.map(nino => (
          <div 
            key={nino.id} 
            onClick={() => verFicha(nino.id)}
            className="bg-white p-4 rounded-[2rem] border border-slate-100 shadow-sm flex flex-col items-center text-center space-y-3 active:scale-95 transition-all group cursor-pointer"
          >
            <div className={`w-20 h-20 rounded-2xl flex items-center justify-center relative overflow-hidden border-2 ${nino.estatus === 'en_salon' ? 'bg-emerald-50 border-emerald-100' : 'bg-slate-50 border-slate-100'}`}>
               {nino.foto ? <img src={nino.foto} className="w-full h-full object-cover" alt={nino.nombre} /> : <User size={32} className="text-slate-300" />}
               <div className={`absolute bottom-0 w-full text-[7px] text-white font-black py-1 uppercase ${nino.estatus === 'en_salon' ? 'bg-emerald-500' : 'bg-slate-400'}`}>
                 {nino.salon}
               </div>
            </div>
            <p className="text-xs font-black text-slate-800 leading-none uppercase italic">{nino.nombre.split(' ')[0]}</p>
          </div>
        ))}
      </div>
    </div>
  );

  // VISTA 2: FICHA DE IDENTIDAD (CUSTODIA)
  const renderResultado = () => (
    <div className="space-y-6 animate-in zoom-in-95 duration-500 pb-10 text-left">
      <button onClick={() => setModo('manual')} className="flex items-center gap-2 text-slate-400 font-black text-[10px] uppercase bg-white px-5 py-2 rounded-xl shadow-sm">
        <ArrowLeft size={14} /> Volver
      </button>

      {ninoInfo && (
        <div className="bg-white rounded-[4rem] shadow-2xl overflow-hidden border-4 border-slate-50">
          <div className={`p-8 text-white flex flex-col items-center relative ${ninoInfo.estatus === 'en_salon' ? 'bg-emerald-600' : 'bg-[#0A2440]'}`}>
            <div className="w-32 h-32 bg-white rounded-[2.5rem] p-1 shadow-2xl mb-4 overflow-hidden">
              <div className="w-full h-full bg-slate-50 rounded-[2.2rem] flex items-center justify-center text-slate-300">
                {ninoInfo.foto ? <img src={ninoInfo.foto} className="w-full h-full object-cover" /> : <User size={60} />}
              </div>
            </div>
            <h3 className="text-2xl font-black italic uppercase">{ninoInfo.nombre}</h3>
            <span className="text-[10px] font-black opacity-80 uppercase tracking-widest bg-black/20 px-4 py-1 rounded-full mt-2">
              {ninoInfo.salon}
            </span>
          </div>

          <div className="p-8 space-y-6">
            <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-3xl border border-slate-100">
              <MapPin className="text-indigo-500 shrink-0" size={24} />
              <div className="text-left">
                <p className="text-[10px] font-black uppercase text-slate-400">Estado de Ubicación</p>
                <p className="text-base font-black text-slate-800 uppercase italic">
                  {ninoInfo.estatus === 'en_salon' ? `Presente en ${ninoInfo.salon}` : 'Fuera de salón'}
                </p>
              </div>
            </div>

            {ninoInfo.especial && (
              <div className="flex items-start gap-4 p-5 bg-rose-50 rounded-3xl border border-rose-100 animate-pulse">
                <AlertCircle className="text-rose-500 shrink-0" size={24} />
                <div className="text-left">
                  <p className="text-[10px] font-black uppercase text-rose-400">Protocolo Sanitario</p>
                  <p className="text-sm font-bold text-rose-900 leading-relaxed uppercase">
                    {ninoInfo.alerta || "REQUIERE ATENCIÓN ESPECIAL"}
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-3">
               <p className="text-[10px] font-black uppercase text-slate-400 ml-4 italic">Tutores Autorizados</p>
               {ninoInfo.tutores?.map((t, i) => (
                 <div key={i} className="flex items-center justify-between p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100">
                    <span className="text-xs font-black text-indigo-900 uppercase">{t.nombre}</span>
                    <a href={`tel:${t.tel}`} className="p-2 bg-white rounded-lg text-indigo-600 shadow-sm"><Phone size={14} /></a>
                 </div>
               ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex bg-white p-1 rounded-2xl shadow-md border-2 border-slate-50">
        <button 
          onClick={() => setModo('manual')}
          className={`flex-1 py-3 rounded-xl flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all ${
            modo === 'manual' || modo === 'resultado' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400'
          }`}
        >
          <Search size={16} /> Directorio
        </button>
        <button 
          onClick={() => setModo('escaneo')}
          className={`flex-1 py-3 rounded-xl flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all ${
            modo === 'escaneo' ? 'bg-slate-900 text-white shadow-lg' : 'text-slate-400'
          }`}
        >
          <QrCode size={16} /> Scanner
        </button>
      </div>

      {modo === 'manual' && renderManual()}
      {modo === 'resultado' && renderResultado()}
      {modo === 'escaneo' && (
        <div className="h-[40vh] bg-slate-900 rounded-[3rem] border-4 border-slate-800 flex flex-col items-center justify-center text-white space-y-4 shadow-inner">
           <QrCode size={48} className="text-amber-400 animate-pulse" />
           <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-40">Cámara en espera...</p>
           <button onClick={() => {
             const ninoId = ninos[0]?.id;
             if(ninoId) verFicha(ninoId);
           }} className="bg-white/10 px-6 py-2 rounded-full text-[9px] font-black uppercase">Simular Escaneo</button>
        </div>
      )}
    </div>
  );
};

export default BuscadorGlobal;